import { useState } from "react";
import { Checkbox } from '@/shared/components/Checkbox'
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import bigLogo from '@/assets/big-logo.svg';
import RecoveryPassword from '@/pages/auth/RecoveryPassword';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// Swap this import for the real warehouse photo once the asset is added:
// import almacenLoginImg from '@/assets/almacen-login.jpg';

interface LoginAlmacenProps {
  onLogin: (
    email: string,
    password: string,
    accessModule?: 'almacen',
  ) => Promise<{ firstName: string; lastName: string } | void>;
  isLoading: boolean;
  error: string | null;
}

const LoginAlmacen = ({
  onLogin,
  isLoading: externalLoading,
  error: externalError,
}: LoginAlmacenProps) => {
  const navigate = useNavigate();
  const [typeForm, setTypeForm] = useState<"login" | "recovery">("login");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (localError) setLocalError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const getErrorMessage = (err: unknown): string => {
    if (err && typeof err === "object" && "message" in err) {
      const errorObj = err as {
        message: string | string[];
        statusCode?: number;
      };
      if (Array.isArray(errorObj.message)) return errorObj.message.join(". ");
      const msg = errorObj.message as string;
      if (
        msg.toLowerCase().includes("invalid credentials") ||
        errorObj.statusCode === 401
      )
        return "Credenciales incorrectas. Por favor verifica tu correo y contraseña.";
      if (errorObj.statusCode === 403)
        return "No tienes permisos para acceder al módulo de Almacén. Contacta al administrador.";
      if (msg && msg.length < 200) return msg;
    }
    return "Error al iniciar sesión. Por favor intenta nuevamente.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      const userProfile = await onLogin(
        formData.email,
        formData.password,
        "almacen",
      );
      let welcome = "¡Bienvenido de nuevo!";
      if (userProfile && "firstName" in userProfile)
        welcome = `¡Bienvenido de nuevo, ${userProfile.firstName}!`;
      setSuccessMessage(welcome);
      setTimeout(() => navigate("/almacen/dashboard"), 1000);
    } catch (err) {
      setLocalError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = externalError || localError;
  const loading = isLoading || externalLoading;

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* ── Left: form / recovery ─────────────────────────────────────────── */}
      {typeForm === "recovery" ? (
        <RecoveryPassword setTypeForm={setTypeForm} />
      ) : (
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <img src={bigLogo} alt="SLYRSA Logo" className="h-20 w-auto mb-2" />
          </div>

          {/* Heading */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Accede a tu cuenta
            </h1>
            <p className="text-sm text-gray-600">
              Ingresa tu correo y contraseña para iniciar sesión.
            </p>
          </div>

          {/* Success */}
          {successMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
              <svg
                className="h-5 w-5 text-green-600 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium text-green-800">
                {successMessage}
              </p>
            </div>
          )}

          {/* Error */}
          {displayError && !successMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{displayError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Correo electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="correo@example.com"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent transition-all pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center">
              <Checkbox
                id="remember-me"
                name="remember-me"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-700"
              >
                Recordar contraseña
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={estiloDeBoton('primario', 'w-full')}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Iniciando sesión...
                </span>
              ) : (
                "Iniciar sesión"
              )}
            </button>

          </form>

          {/* Forgot password */}
          <div className="mt-4 text-center">
            <p
              onClick={() => setTypeForm("recovery")}
              className="text-sm text-[#3B5998] hover:text-[#344e86] transition-colors cursor-pointer"
            >
              ¿Olvidaste tu contraseña?
            </p>
          </div>
        </div>
      </div>
      )}

      {/* ── Right: image panel ─────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        <div className="rounded-[32px] w-full h-[calc(100vh-3rem)] relative overflow-hidden shadow-xl">
          {/* Background image */}
          <img
            src="/image-login-almacen.png"
            alt="Almacén"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Blue gradient blur overlay — mirrors provider login */}
          <div className="absolute inset-0 rounded-[32px] blur-2xl pointer-events-none bg-gradient-to-t from-[#1FB7EE]/10 to-[#315FB9]/80" />

          {/* "Almacén" badge */}
          <div className="absolute top-5 right-5 bg-white rounded-full px-5 py-1.5 z-20">
            <p className="font-semibold text-sm text-gray-900">Almacén</p>
          </div>

          {/* Description bubble */}
          <div className="absolute top-[120px] left-12 right-12 z-20 max-w-[85%]">
            <div className="bg-white/10 backdrop-blur-sm border border-white rounded-[500px] px-7 py-4 flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-4 h-4 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Administra y optimiza tu almacén en un solo lugar. Controla
                inventarios, movimientos y asignaciones en tiempo real para una
                operación más eficiente.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginAlmacen;
